import React, { useState, useEffect, useRef } from 'react';
const CulturePodPage = ({ API, isDarkMode, showNotification, currentUser, userToken, hasPaidPlan }) => {
  const [categories, setCategories] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);

  // Audio state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [userRating, setUserRating] = useState(0);

  const authHeaders = userToken ? { 'Authorization': `Bearer ${userToken}` } : {};

  const COVER_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
  ];

  const getEpisodeGradient = (episode, fallbackIndex) => {
    if (episode.coverStyle && episode.coverStyle.startsWith('gradient-')) {
      const idx = parseInt(episode.coverStyle.split('-')[1]) || 0;
      return COVER_GRADIENTS[idx % COVER_GRADIENTS.length];
    }
    return COVER_GRADIENTS[fallbackIndex % COVER_GRADIENTS.length];
  };

  const GradientCover = ({ episode, index, height = 160, showTitle = true }) => {
    const gradient = getEpisodeGradient(episode, index);
    const subtitle = episode.coverSubtitle || categories.find(c => c.id === episode.categoryId)?.name || '';
    return (
      <div style={{ width: '100%', height, background: gradient, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative' }}>
        <div style={{ fontSize: 48, opacity: 0.3, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>🎙️</div>
        {subtitle && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, zIndex: 1 }}>{subtitle}</div>}
        {showTitle && <div style={{ fontSize: 15, color: '#fff', fontWeight: 700, textAlign: 'center', zIndex: 1, textShadow: '0 1px 4px rgba(0,0,0,0.3)', maxWidth: '90%' }}>{episode.title}</div>}
      </div>
    );
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [setRes, catRes, epRes] = await Promise.all([
          fetch(`${API}/culture-pod/settings`),
          fetch(`${API}/culture-pod/categories`),
          fetch(`${API}/culture-pod/episodes`)
        ]);
        if (setRes.ok) setSettings(await setRes.json());
        if (catRes.ok) { const d = await catRes.json(); setCategories(d.categories || []); }
        if (epRes.ok) { const d = await epRes.json(); setEpisodes(d.episodes || []); }

        // Load progress if logged in
        if (userToken) {
          try {
            const pRes = await fetch(`${API}/culture-pod/progress`, { headers: { 'Authorization': `Bearer ${userToken}` } });
            if (pRes.ok) {
              const pd = await pRes.json();
              const map = {};
              (pd.progress || []).forEach(p => { map[p.episodeId] = p; });
              setUserProgress(map);
            }
          } catch (e) { /* ignore */ }
        }
      } catch (e) { console.error('Load error:', e); }
      setLoading(false);
    };
    load();
  }, [API, userToken]);

  // Audio time update
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoad = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); setShowQuiz(true); saveProgress({ completed: true }); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoad);
    audio.addEventListener('ended', onEnded);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('loadedmetadata', onLoad); audio.removeEventListener('ended', onEnded); };
  }, [selectedEpisode]);


  const saveProgress = async (data) => {
    if (!userToken || !selectedEpisode) return;
    try {
      await fetch(`${API}/culture-pod/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ episodeId: selectedEpisode.id, ...data })
      });
    } catch (e) { /* ignore */ }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); } else { audio.play().catch(() => {}); }
    setIsPlaying(!isPlaying);
  };

  const seekTo = (time) => {
    const audio = audioRef.current;
    if (audio) { audio.currentTime = time; setCurrentTime(time); }
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const addBookmark = () => {
    const label = `Bookmark at ${formatTime(currentTime)}`;
    saveProgress({ addBookmark: { time: currentTime, label } });
    showNotification('Bookmark saved', 'success');
  };

  const submitRating = (stars) => {
    setUserRating(stars);
    saveProgress({ rating: stars });
    showNotification(`Rated ${stars} stars`, 'success');
  };

  const submitQuiz = () => {
    setQuizSubmitted(true);
    const qs = selectedEpisode.quizQuestions || [];
    let correct = 0;
    qs.forEach((q, i) => { if (quizAnswers[i] === q.correctIndex) correct++; });
    saveProgress({ quizScore: correct });
  };

  const openEpisode = (ep) => {
    if (!ep.isFree && !hasPaidPlan) {
      showNotification('This episode requires a Pro plan', 'info');
      return;
    }
    setSelectedEpisode(ep);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setCurrentTime(0);
    setIsPlaying(false);
    setUserRating(userProgress[ep.id]?.rating || 0);
  };

  const filteredEpisodes = selectedCategory === 'all' ? episodes : episodes.filter(e => e.categoryId === selectedCategory);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading Culture Pod...</div>;

  // ── EPISODE VIEW ──
  if (selectedEpisode) {
    const ep = selectedEpisode;
    const transcript = ep.transcript || [];
    const currentTranscriptIdx = transcript.findIndex((t, i) => {
      const next = transcript[i + 1];
      return currentTime >= t.startTime && (!next || currentTime < next.startTime);
    });
    // Find image matching current time — scan all transcript images
    let currentImage = null;
    for (const t of transcript) {
      for (const img of (t.images || [])) {
        if (img.url && currentTime >= img.startTime && currentTime < img.endTime) { currentImage = img.url; break; }
      }
      if (currentImage) break;
    }

    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 16px 40px' }}>
        {/* Back button */}
        <button onClick={() => { setSelectedEpisode(null); if (audioRef.current) { audioRef.current.pause(); } setIsPlaying(false); }} style={{ background: 'none', border: 'none', color: '#8b5cf6', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: '16px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          &larr; Back to episodes
        </button>

        {/* Title + Audio controls */}
        <div style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 16, padding: 24, marginBottom: 16, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
          <h1 style={{ color: isDarkMode ? '#f1f5f9' : '#1e293b', fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{ep.title}</h1>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 16 }}>{ep.coverSubtitle || ''}{ep.coverSubtitle ? ' | ' : ''}{ep.duration} | {ep.isFree ? 'FREE' : 'PRO'}</p>

          <audio ref={audioRef} src={ep.audioUrl} preload="metadata" />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={togglePlay} style={{ width: 48, height: 48, borderRadius: '50%', border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isPlaying ? '||' : '\u25B6'}
            </button>
            <div style={{ flex: 1 }}>
              <input type="range" min={0} max={duration || 100} value={currentTime} onChange={e => seekTo(Number(e.target.value))} style={{ width: '100%', accentColor: '#8b5cf6' }} />
            </div>
            <span style={{ color: isDarkMode ? '#e2e8f0' : '#475569', fontSize: 13, minWidth: 80 }}>{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>

          {/* Chapters */}
          {(ep.chapters || []).length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {ep.chapters.map((ch, i) => {
                const isActive = ep.chapters[i + 1] ? (currentTime >= ch.timestamp && currentTime < ep.chapters[i + 1].timestamp) : currentTime >= ch.timestamp;
                return (
                  <button key={i} onClick={() => seekTo(ch.timestamp)} style={{ padding: '6px 14px', borderRadius: 20, border: 'none', background: isActive ? '#8b5cf6' : (isDarkMode ? '#0f172a' : '#f1f5f9'), color: isActive ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'), fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                    {ch.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Split-screen: Image/GIF left + Transcript right */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          {/* LEFT: Image/GIF area */}
          <div style={{ flex: '1 1 400px', minHeight: 300, borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
            {currentImage ? (
              <img src={currentImage} alt="" style={{ width: '100%', height: '100%', minHeight: 300, objectFit: 'cover', transition: 'opacity 0.8s ease-in-out', borderRadius: 16 }} />
            ) : ep.coverImageUrl ? (
              <img src={ep.coverImageUrl} alt="" style={{ width: '100%', height: '100%', minHeight: 300, objectFit: 'cover', borderRadius: 16 }} />
            ) : (
              <div style={{ width: '100%', height: '100%', minHeight: 300, background: getEpisodeGradient(ep, episodes.indexOf(ep)), borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 64, opacity: 0.4 }}>🎙️</div>
                {(ep.coverSubtitle || categories.find(c => c.id === ep.categoryId)?.name) && (
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginTop: 8 }}>{ep.coverSubtitle || categories.find(c => c.id === ep.categoryId)?.name}</div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Transcript */}
          {transcript.length > 0 && (
            <div style={{ flex: '1 1 300px', background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 12, padding: 20, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, maxHeight: 400, overflowY: 'auto' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: 12 }}>What the host is saying:</h3>
              {transcript.map((t, i) => (
                <p key={i} onClick={() => seekTo(t.startTime)} style={{ padding: '8px 12px', margin: '2px 0', borderRadius: 8, cursor: 'pointer', fontSize: 15, lineHeight: 1.6, transition: 'all 0.3s', borderLeft: i === currentTranscriptIdx ? '3px solid #8b5cf6' : '3px solid transparent', background: i === currentTranscriptIdx ? (isDarkMode ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)') : 'transparent', color: i === currentTranscriptIdx ? (isDarkMode ? '#f1f5f9' : '#1e293b') : i < currentTranscriptIdx ? '#64748b' : (isDarkMode ? '#94a3b8' : '#94a3b8'), fontWeight: i === currentTranscriptIdx ? 600 : 400 }}>
                  {t.text}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Bookmark button */}
        <div style={{ marginBottom: 20, textAlign: 'center' }}>
          <button onClick={addBookmark} style={{ background: 'none', border: '1px solid #8b5cf6', color: '#8b5cf6', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Bookmark this moment
          </button>
        </div>

        {/* Darija Phrases */}
        {(ep.phrases || []).length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>Darija Phrases</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {ep.phrases.map((p, i) => (
                <div key={i} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 12, padding: 16, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', marginBottom: 4 }}>{p.latin}</div>
                  <div style={{ fontSize: 18, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 4, direction: 'rtl' }}>{p.arabic}</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{p.english}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quiz */}
        {showQuiz && (ep.quizQuestions || []).length > 0 && (
          <div style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 12, padding: 24, marginBottom: 24, border: `2px solid #8b5cf6` }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 16 }}>Quick Quiz</h3>
            {ep.quizQuestions.map((q, qi) => (
              <div key={qi} style={{ marginBottom: 20 }}>
                <p style={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 8 }}>{qi + 1}. {q.question}</p>
                {(q.options || []).map((opt, oi) => {
                  const selected = quizAnswers[qi] === oi;
                  const isCorrect = q.correctIndex === oi;
                  let bg = isDarkMode ? '#0f172a' : '#f8fafc';
                  if (quizSubmitted && selected && isCorrect) bg = '#22c55e33';
                  else if (quizSubmitted && selected && !isCorrect) bg = '#ef444433';
                  else if (quizSubmitted && isCorrect) bg = '#22c55e22';
                  return (
                    <button key={oi} onClick={() => !quizSubmitted && setQuizAnswers({ ...quizAnswers, [qi]: oi })} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4, borderRadius: 8, border: selected ? '2px solid #8b5cf6' : `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, background: bg, color: isDarkMode ? '#e2e8f0' : '#1e293b', cursor: quizSubmitted ? 'default' : 'pointer', fontSize: 14 }}>
                      {opt} {quizSubmitted && isCorrect && ' \u2713'}
                    </button>
                  );
                })}
              </div>
            ))}
            {!quizSubmitted ? (
              <button onClick={submitQuiz} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Submit Quiz</button>
            ) : (
              <p style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
                Score: {ep.quizQuestions.filter((q, i) => quizAnswers[i] === q.correctIndex).length}/{ep.quizQuestions.length}
              </p>
            )}
          </div>
        )}

        {/* Rating */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: 8 }}>Rate this episode:</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => submitRating(s)} style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: s <= userRating ? '#f59e0b' : '#475569' }}>
                {s <= userRating ? '\u2605' : '\u2606'}
              </button>
            ))}
          </div>
        </div>

        {/* Related Episodes */}
        {episodes.filter(e => e.id !== ep.id && e.categoryId === ep.categoryId).length > 0 && (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>Related Episodes</h3>
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
              {episodes.filter(e => e.id !== ep.id && e.categoryId === ep.categoryId).slice(0, 4).map(re => (
                <div key={re.id} onClick={() => openEpisode(re)} style={{ minWidth: 160, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', background: isDarkMode ? '#1e293b' : '#fff', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
                  {re.coverImageUrl ? <img src={re.coverImageUrl} alt="" style={{ width: '100%', height: 90, objectFit: 'cover' }} /> : <GradientCover episode={re} index={episodes.indexOf(re)} height={90} showTitle={false} />}
                  <div style={{ padding: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>{re.title}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{re.duration}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── EPISODE LIST VIEW (Netflix-style cards) ──
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: isDarkMode ? '#f1f5f9' : '#1e293b', marginBottom: 4 }}>{settings.headerTitle || 'Culture Pod'}</h1>
        <p style={{ color: '#94a3b8', fontSize: 16 }}>{settings.headerSubtitle || 'Discover Morocco through stories'}</p>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={() => setSelectedCategory('all')} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', background: selectedCategory === 'all' ? '#8b5cf6' : (isDarkMode ? '#1e293b' : '#f1f5f9'), color: selectedCategory === 'all' ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'), fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>All</button>
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', background: selectedCategory === cat.id ? '#8b5cf6' : (isDarkMode ? '#1e293b' : '#f1f5f9'), color: selectedCategory === cat.id ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'), fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {cat.icon} {cat.name}
          </button>
        ))}
      </div>

      {/* Episode cards grid */}
      {filteredEpisodes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎧</div>
          <p>No episodes yet. Coming soon!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
          {filteredEpisodes.map((ep, epIdx) => {
            const progress = userProgress[ep.id];
            const cat = categories.find(c => c.id === ep.categoryId);
            return (
              <div key={ep.id} onClick={() => openEpisode(ep)} style={{ borderRadius: 16, overflow: 'hidden', cursor: 'pointer', background: isDarkMode ? '#1e293b' : '#fff', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(139,92,246,0.2)'; }} onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}>
                {/* Cover image */}
                <div style={{ position: 'relative', height: 160, background: '#0f172a' }}>
                  {ep.coverImageUrl ? (
                    <img src={ep.coverImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <GradientCover episode={ep} index={epIdx} />
                  )}
                  {/* Pro badge */}
                  {!ep.isFree && (
                    <div style={{ position: 'absolute', top: 8, right: 8, background: '#f59e0b', color: '#000', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>PRO</div>
                  )}
                  {/* Free badge */}
                  {ep.isFree && (
                    <div style={{ position: 'absolute', top: 8, right: 8, background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>FREE</div>
                  )}
                  {/* Completed check */}
                  {progress?.completed && (
                    <div style={{ position: 'absolute', top: 8, left: 8, background: '#22c55e', color: '#fff', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{'\u2713'}</div>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: 16 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b', marginBottom: 4 }}>{ep.title}</div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>{cat?.icon} {cat?.name || ''} | {ep.duration}</div>
                  {ep.description && <div style={{ fontSize: 13, color: isDarkMode ? '#94a3b8' : '#64748b', lineHeight: 1.4 }}>{ep.description.substring(0, 100)}{ep.description.length > 100 ? '...' : ''}</div>}
                  {/* Progress bar */}
                  {progress && !progress.completed && progress.currentTime > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ height: 3, borderRadius: 2, background: isDarkMode ? '#334155' : '#e2e8f0' }}>
                        <div style={{ height: '100%', borderRadius: 2, background: '#8b5cf6', width: `${Math.min(100, (progress.currentTime / (duration || 1)) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CulturePodPage;
